import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";

/**
 * ThemeContext
 * - Full-featured theme state with localStorage persistence
 * - System dark mode support
 * - Saved themes CRUD
 * - Export / Import
 * - Notification sub-theme
 * - CSS variables injection for the app
 */

/* =========================
 * Defaults and constants
 * ========================= */
const STORAGE_KEY = "theme-settings";
const SAVED_THEMES_KEY = "saved-themes";

export const DEFAULT_THEME = {
  mode: "system",          // "light" | "dark" | "system"
  contrast: "normal",      // "normal" | "high"
  accent: "#3b82f6",       // Tailwind-ish default blue-500
  bg: "#ffffff",
  fontSize: 16,            // px
  fontFamily: "system",    // "system" | "serif" | "mono" | "rounded"
  buttonShape: "rounded",  // "rounded" | "pill" | "square"
  spacing: "comfortable",  // "compact" | "comfortable" | "spacious"
  animations: "smooth",    // "none" | "subtle" | "smooth" | "bouncy"
  shadows: "medium",       // "none" | "subtle" | "medium" | "elevated"

  // Notification UI sub-theme (toast/dialog/etc.)
  notifTheme: {
    position: "top-right",      // "top-right" | "top-left" | "bottom-right" | "bottom-left"
    shape: "rounded",           // "rounded" | "pill" | "square"
    background: "#111827",      // slate-900
    text: "#ffffff",
    border: "none",             // "none" | "thin" | "thick"
    shadow: "elevated",         // "none" | "subtle" | "elevated"
    animation: "slide",         // "none" | "fade" | "slide" | "bounce"
    autoCloseMs: 3500
  }
};

/* =========================
 * Utilities
 * ========================= */

// clamp a number
const clamp = (n, min, max) => Math.min(max, Math.max(min, n));

// simple brightness adjuster (-100..100)
export function adjustBrightness(hex, percent) {
 try {
   const num = parseInt(hex.replace("#", ""), 16);
   const amt = Math.round(2.55 * percent);
   const r = clamp((num >> 16) + amt, 0, 255);
   const g = clamp(((num >> 8) & 0x00ff) + amt, 0, 255);
   const b = clamp((num & 0x0000ff) + amt, 0, 255);
   const out = (1 << 24) + (r << 16) + (g << 8) + b;
   return `#${out.toString(16).slice(1)}`;
 } catch {
   return hex;
 }
}

// generate a tiny palette from an accent
function buildAccentPalette(accent) {
  return {
    "--acc-100": adjustBrightness(accent, 45),
    "--acc-200": adjustBrightness(accent, 30),
    "--acc-300": adjustBrightness(accent, 15),
    "--acc-400": accent,
    "--acc-500": adjustBrightness(accent, -10),
    "--acc-600": adjustBrightness(accent, -20),
  };
}

// map logical tokens to CSS vars
function themeToCssVars(theme, effectiveMode) {
  const isDark = effectiveMode === "dark";
  const isHighContrast = theme.contrast === "high";

  // base surfaces
  const surface = isDark ? "#0b1220" : theme.bg || "#ffffff";
  const surfaceMuted = isDark ? "#111827" : adjustBrightness(surface, -2);
  const text = isDark ? "#e5e7eb" : "#111827";
  const textMuted = isDark ? "#cbd5e1" : "#374151";
  const border = isDark ? "#1f2937" : "#e5e7eb";

  // spacing scale
  const spacingBase = theme.spacing === "compact" ? 6 : theme.spacing === "spacious" ? 12 : 8;

  // radius
  const radius =
    theme.buttonShape === "square" ? 4 : theme.buttonShape === "pill" ? 999 : 8;

  // motion
  const dur =
    theme.animations === "none" ? "0ms"
      : theme.animations === "subtle" ? "120ms"
      : theme.animations === "smooth" ? "220ms"
      : "320ms";
  const ease =
    theme.animations === "bouncy" ? "cubic-bezier(.2,.8,.2,1.2)" : "cubic-bezier(.2,.8,.2,1)";

  // elevation
  const shadow =
    theme.shadows === "none" ? "none"
      : theme.shadows === "subtle" ? "0 1px 2px rgba(0,0,0,.06)"
      : theme.shadows === "medium" ? "0 4px 12px rgba(0,0,0,.12)"
      : "0 12px 28px rgba(0,0,0,.18)";

  // font family
  const families = {
    system: `ui-sans-serif, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, "Noto Sans", "Apple Color Emoji", "Segoe UI Emoji"`,
    serif: `ui-serif, Georgia, Cambria, "Times New Roman", Times, serif`,
    mono: `ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace`,
    rounded: `"Inter var", ui-rounded, "SF Pro Rounded", ui-sans-serif, system-ui`,
  };

  const palette = buildAccentPalette(theme.accent);

  return {
    "--bg": surface,
    "--bg-muted": surfaceMuted,
    "--text": text,
    "--text-muted": textMuted,
    "--border": border,

    // contrast tweaks
    "--focus-ring":
      isHighContrast ? theme.accent : adjustBrightness(theme.accent, isDark ? 35 : -35),

    // type scale
    "--font-size": `${clamp(theme.fontSize, 12, 24)}px`,
    "--font-family": families[theme.fontFamily] || families.system,
    "--radius": `${radius}px`,

    // spacing scale derived from base
    "--space-1": `${Math.round(spacingBase * 0.75)}px`,
    "--space-2": `${spacingBase}px`,
    "--space-3": `${Math.round(spacingBase * 1.5)}px`,
    "--space-4": `${spacingBase * 2}px`,

    // motion
    "--transition-fast": dur,
    "--ease": ease,

    // elevation
    "--shadow": shadow,

    // accent palette
    ...palette,
  };
}

// read JSON safely
function readJson(key, fallback) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;
    return JSON.parse(raw);
  } catch {
    return fallback;
  }
}

/* =========================
 * Context
 * ========================= */
const ThemeContext = createContext(null);

/* =========================
 * Provider
 * ========================= */
export function ThemeProvider({ children }) {
  // load theme with migration to current schema
  const [theme, setTheme] = useState(() => {
    const stored = readJson(STORAGE_KEY, null);
    if (!stored) return DEFAULT_THEME;

    return {
      ...DEFAULT_THEME,
      ...stored,
      notifTheme: {
        ...DEFAULT_THEME.notifTheme,
        ...(stored.notifTheme || {})
      }
    };
  });

  const [savedThemes, setSavedThemes] = useState(() => {
    const list = readJson(SAVED_THEMES_KEY, []);
    return Array.isArray(list) ? list : [];
  });

  const [previewMode, setPreviewMode] = useState(null); // "apply" | "compare" | null
  const mediaRef = useRef(null);

  // react to system appearance when mode === 'system'
  useEffect(() => {
    if (!window || !window.matchMedia) return;
    mediaRef.current = window.matchMedia("(prefers-color-scheme: dark)");
    const listener = () => {
      if (theme.mode === "system") {
        applyThemeToDocument(theme);
      }
    };
    mediaRef.current.addEventListener?.("change", listener);
    return () => mediaRef.current?.removeEventListener?.("change", listener);
  }, [theme.mode]); // eslint-disable-line react-hooks/exhaustive-deps

  // persist theme
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(theme));
    applyThemeToDocument(theme);
  }, [theme]);

  // persist saved themes
  useEffect(() => {
    localStorage.setItem(SAVED_THEMES_KEY, JSON.stringify(savedThemes));
  }, [savedThemes]);

  // effective mode (resolves "system")
  const effectiveMode = useMemo(() => {
    if (theme.mode !== "system") return theme.mode;
    if (!window || !window.matchMedia) return "light";
    return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
  }, [theme.mode]);

  // computed CSS variables
  const cssVars = useMemo(() => themeToCssVars(theme, effectiveMode), [theme, effectiveMode]);

  // inject CSS vars onto :root
  function applyThemeToDocument(nextTheme) {
    const root = document.documentElement;
    const mode = nextTheme.mode === "system"
      ? (window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light")
      : nextTheme.mode;

    root.dataset.themeMode = mode;
    root.dataset.contrast = nextTheme.contrast;

    const vars = themeToCssVars(nextTheme, mode);
    Object.entries(vars).forEach(([k, v]) => root.style.setProperty(k, v));
  }

  /* =========================
   * Setters (fine-grained)
   * ========================= */
  const setMode = (mode) => setTheme(prev => ({ ...prev, mode }));
  const setContrast = (contrast) => setTheme(prev => ({ ...prev, contrast }));
  const setAccent = (accent) => setTheme(prev => ({ ...prev, accent }));
  const setBg = (bg) => setTheme(prev => ({ ...prev, bg }));
  const setFontSize = (fontSize) =>
    setTheme(prev => ({ ...prev, fontSize: clamp(fontSize, 12, 24) }));
  const setFontFamily = (fontFamily) => setTheme(prev => ({ ...prev, fontFamily }));
  const setButtonShape = (buttonShape) => setTheme(prev => ({ ...prev, buttonShape }));
  const setSpacing = (spacing) => setTheme(prev => ({ ...prev, spacing }));
  const setAnimations = (animations) => setTheme(prev => ({ ...prev, animations }));
  const setShadows = (shadows) => setTheme(prev => ({ ...prev, shadows }));

  const setNotifTheme = (notifTheme) => setTheme(prev => ({
    ...prev,
    notifTheme: { ...prev.notifTheme, ...notifTheme }
  }));

  /* =========================
   * Helpers / Actions
   * ========================= */

  // quick random accent
  const randomizeAccent = () => {
    const hue = Math.floor(Math.random() * 360);
    // pleasant saturation/lightness
    const color = hslToHex(hue, 72, 52);
    setAccent(color);
  };

  // export current theme as JSON (object)
  const exportTheme = () => {
    const data = {
      ...theme,
      exportedAt: new Date().toISOString(),
      version: "2.0",
      platform: "PA Exam Platform"
    };
    return data;
  };

  // import theme from object (merge with defaults)
  const importTheme = (themeData) => {
    try {
      const imported = {
        ...DEFAULT_THEME,
        ...themeData,
        notifTheme: {
          ...DEFAULT_THEME.notifTheme,
          ...(themeData?.notifTheme || {})
        }
      };
      setTheme(imported);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: e?.message || "Invalid theme data" };
    }
  };

  // reset to defaults
  const resetTheme = () => setTheme(DEFAULT_THEME);

  // Saved themes CRUD
  const saveCurrentTheme = (name) => {
    const id = cryptoRandomId();
    const entry = { id, name: name || "Untitled Theme", data: { ...theme } };
    setSavedThemes(prev => [entry, ...prev]);
    return id;
  };

  const updateSavedTheme = (id, patch) => {
    setSavedThemes(prev =>
      prev.map(t => (t.id === id ? { ...t, ...patch } : t))
    );
  };

  const deleteSavedTheme = (id) => setSavedThemes(prev => prev.filter(t => t.id !== id));

  const duplicateSavedTheme = (id) => {
    const src = savedThemes.find(t => t.id === id);
    if (!src) return null;
    const newId = cryptoRandomId();
    setSavedThemes(prev => [{ id: newId, name: `${src.name} (Copy)`, data: { ...src.data } }, ...prev]);
    return newId;
  };

  const loadSavedTheme = (id) => {
    const found = savedThemes.find(t => t.id === id);
    if (!found) return false;
    importTheme(found.data);
    return true;
  };

  /* =========================
   * Derived values
   * ========================= */
  const isDark = effectiveMode === "dark";
  const accentPalette = useMemo(() => buildAccentPalette(theme.accent), [theme.accent]);

  /* =========================
   * Context value
   * ========================= */
  const value = {
    // state
    theme,
    isDark,
    effectiveMode,
    previewMode,
    savedThemes,
    cssVars,
    accentPalette,

    // setters
    setTheme, // escape hatch
    setMode,
    setContrast,
    setAccent,
    setBg,
    setFontSize,
    setFontFamily,
    setButtonShape,
    setSpacing,
    setAnimations,
    setShadows,
    setNotifTheme,
    setPreviewMode,

    // actions
    applyThemeToDocument,
    randomizeAccent,
    resetTheme,
    exportTheme,
    importTheme,

    // saved themes
    saveCurrentTheme,
    updateSavedTheme,
    deleteSavedTheme,
    duplicateSavedTheme,
    loadSavedTheme,
  };

  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
}

/* =========================
 * Hook
 * ========================= */
export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme must be used within <ThemeProvider>");
  return ctx;
}

/* =========================
 * Small helpers
 * ========================= */

function cryptoRandomId() {
  // Browser-safe short id (no external deps)
  if (window?.crypto?.getRandomValues) {
    const arr = new Uint32Array(2);
    window.crypto.getRandomValues(arr);
    return [...arr].map(n => n.toString(36)).join("").slice(0, 12);
  }
  return Math.random().toString(36).slice(2, 14);
}

function hslToHex(h, s, l) {
  s /= 100;
  l /= 100;
  const k = n => (n + h / 30) % 12;
  const a = s * Math.min(l, 1 - l);
  const f = n =>
    l - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)));
  const r = Math.round(255 * f(0));
  const g = Math.round(255 * f(8));
  const b = Math.round(255 * f(4));
  return `#${[r, g, b].map(x => x.toString(16).padStart(2, "0")).join("")}`;
}

export default ThemeProvider;
